import React, { useState, useEffect, useCallback, useContext } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import Moment from 'moment';
import {
    get as _get,
    isEqual as _isEqual,
} from 'lodash';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import GetAppIcon from '@mui/icons-material/GetApp';
import AddIcon from '@mui/icons-material/Add';
import PollIcon from '@mui/icons-material/Poll';

import SearchBar from '../../../components/searchbar';
import SensorName from '../../../components/sensorname';
import ConfirmationDialog from '../../../components/confirmationdlg';
import CellExpand from '../../../components/xgrid/cell-expand';
import PopupMenu from '../../../components/popupmenu';
import LabeledToolbar from '../../../components/styled/toolbar';
import { LoadingOverlay, NoCMCLoadingOverlay, NoRowsOverlay } from '../../../components/xgrid/overlays';
import ProgressBar from '../../../components/progress-bar';

import Auth, { User } from '../../../data/auth-service';
import PcapService, { Fields } from '../../../data/pcap-service';
import { FormatBytesM } from '../../../data/format';
import DataContext from '../../../data/data-context';
import { purple, olive } from '../../../data/colors';
import CMCsDataService, { CmcContext } from '../../../data/cmcs-service';

import { TimeDiffShort } from '../../../libraries/helpers';
import useWindowSize from '../../../libraries/usewindowsize';

const useStyles = makeStyles(theme => ({
    root: {
    },
    toolbar: {
        padding: theme.spacing(1),
        borderBottom: `1px solid rgba(${theme.palette.mode === 'dark' ? '81, 81, 81' : '224, 224, 224'}, 1)`,
    },
    header: {
        width: '100%',
        margin: theme.spacing(1),
        paddingBottom: theme.spacing(2),
    },
    buttonRight: {
        float: 'right',
        marginRight: theme.spacing(3),
    },
    // progress styles
    progress: {
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: 26,
        borderRadius: 2,
    },
    value: {
        position: 'absolute',
        lineHeight: '24px',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.text.primary,
        '&.low': {
            color: theme.palette.mode === 'light' ? 'rgba(100, 100, 100, 1)' : '#FFF',
        },
        '&.medium, &.high': {
            color: theme.palette.mode === 'light' ? 'rgba(175, 175, 175, 1)' : '#FFF',
        },
        '&.done': {
            color: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 1)' : '#FFF',
        },
    },
    bar: {
        height: '100%',
        '&.low': {
            backgroundColor: purple[100],
        },
        '&.medium': {
            backgroundColor: purple[200],
        },
        '&.high': {
            backgroundColor: purple[300],
        },
        '&.done': {
            backgroundColor: purple[400],
        },
    },
    chip: {
        textAlign: 'center',
        height: 26,
        '& .MuiChip-label': {
            width: 80,
        },
        '&:not(.outlined)': {
            color: 'black',
        },
        '&.outlined': {
            color: theme.palette.text.primary,
        },
        '&.done': {
            backgroundColor: olive[400],
        },
    },
}));

const COL_DEFAULT = {
    resizable: true,
    sortable: true,
};

const TOOLBAR_SIZE = 240;
const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

const runningStates = {
    running: true,
    queued: true,
    cancelled: false,
    done: false,
};

const PacketRequests = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const { sensorNames } = useContext(DataContext);

    const tableAPIRef = useApiRef();
    const [selection, setSelection] = useState();
    const [data, setData] = useState([]);
    const [tableHeight, setTableHeight] = useState(800);
    const [columns, setColumns] = useState([
        {
            resizable: true,
            sortable: true,
            variable: 'created',
            title: 'Created',
            field: 'created',
            type: 'dateTime',
            headerName: 'Created',
            renderCell: r => {
                const d = r.row;
                if (d.created === '0001-01-01T00:00:00Z') return '';
                const v = Moment(d.created);
                return v.isValid() ? Moment(v).format(theme.date.long) : v;
            },
            valueGetter: r => {
                const d = r.row;
                if (!d.created || d.created === '0001-01-01T00:00:00Z') return '';
                return d.created;
            },
            width: 150,
        }
    ]);
    const [deleteConfrm, setDeleteConfrm] = useState(false);
    const [search, setSearch] = useState('');
    const [pcapSort, setPCAPSort] = useState([{ field: 'created', sort: 'desc' }]);

    const [timeoutID, setTimeoutID] = useState();
    const { height } = useWindowSize();
    const user = User();
    const pcapPerms = Auth.permissions.get('Pcap');

    // cmc is an object { uuid, name } - stored in page profile as uuid
    const { cmc, setCmc } = useContext(CmcContext);
    // cmcs is an object { uuid: name, uuid: name }
    const [cmcs, setCmcs] = useState();

    const handleUpdatePCAPSort = s => {
        if (!_isEqual(s, pcapSort)) {
            setPCAPSort(s);
        }
    };

    const handleSearchChange = ({ search }) => setSearch(search);

    const getFilteredData = () => {
        if (!search) return data;

        const match = s => (s || '').toLowerCase().indexOf(search.toLowerCase()) >= 0;

        return data.filter(d => {
            return match(d.host) ||
                Object.entries((d.params || {})).some(([k, v]) => {
                    let val = v || '';
                    if (Array.isArray(v)) val = v.join('');
                    return match(val);
                }) ||
                match(d.state) ||
                match(d.user) ||
                (sensorNames[d.sensor_uuid] && match(sensorNames[d.sensor_uuid].name));
        });
    };

    const handleOnRequestPcap = () => navigate('/views/packet_requests/create_pcap');

    const handleOnDelete = () => {
        setDeleteConfrm(false);
        if (!selection || !selection.row) return;
        PcapService.cancel(selection.row.host, selection.row.id, cmc).then(res => {
            if ((res || '').startsWith('error:')) {
                toast.error(res);
            }
            const i = data.findIndex(p => p.id === selection.row.id);
            // something something about the grid freezing the array...
            const d = [...data];
            if (i >= 0) d.splice(i, 1);
            setData(d);
            setSelection();
        }).catch(handleError);
    };

    const handleOnRefresh = () => {
        if (!selection || !selection.row) return;
        PcapService.query(selection.row.host, selection.row.id, cmc).then(res => {
            if (!res) return;
            const i = data.findIndex(p => p.id === selection.row.id);
            if (i >= 0) {
                res.state = res.state === 'done' && res.size <= 0 ? 'empty' : res.state;
                res.sensor_uuid = data[i].sensor_uuid;
                // something something about the grid freezing the array...
                const d = [...data];
                d[i] = res;
                setData(d);
            }
        }).catch(handleError);
    };

    const handleOnDownload = () => {
        if (!selection || !selection.row) return;
        PcapService.download(
            selection.row.host,
            selection.row.id,
            'capture_' + selection.row.host + '.pcap',
            cmc);

        const i = data.findIndex(p => p.id === selection.row.id);
        // something something about the grid freezing the array...
        const d = [...data];
        if (i >= 0) d.splice(i, 1);
        setData(d);
        setSelection();
    };

    const fetchData = useCallback(async () => {
        let d = await PcapService.list(cmc).catch(handleError);

        // this will save a lot of cycles in sensor name lookup and sorting
        const sensorHostMap = Object.entries(sensorNames)
            .reduce((a, [k, v]) => ({ ...a, [v.host]: { ...v, uuid: k } }), {});

        d = (d || []).map(d => {
            if (sensorHostMap[d.host]) d.sensor_uuid = sensorHostMap[d.host].uuid;
            d.state = d.state === 'done' && d.size <= 0 ? 'empty' : d.state;
            return d;
        });
        setData(d);

        // update progress bar until they are all done
        if (d.some(p => p.progress < 100 && runningStates[p.state])) {
            setTimeoutID(current => {
                clearTimeout(current);
                return setTimeout(() => {
                    fetchData();
                }, 11000);
            });
        }
    }, [cmc, sensorNames]);

    const fetchCMCs = useCallback(async () => {
        const cmcs = await CMCsDataService.getNames();
        setCmcs(cmcs);
    }, []);

    const handleError = error => {
        if (error && error.message === 'no cmc') {
            return;
        }
        console.error(error);
        toast.error(`There was an error processing your request, ${error}`);
    };

    const handleColumnReorder = params => {
        setColumns(tableAPIRef.current.getAllColumns());
    };

    const handleColumnResize = params => {
        setColumns(tableAPIRef.current.getAllColumns());
    };

    useEffect(() => {
        if (MC2CHECK) fetchCMCs();
    }, [fetchCMCs]);

    useEffect(() => {
        return () => {
            clearTimeout(timeoutID);
        };
    }, [timeoutID]);

    // size hook
    useEffect(() => {
        setTableHeight(Math.max(height - TOOLBAR_SIZE, 300));
    }, [height]);

    useEffect(() => {
        if (!Object.keys((sensorNames || {})).length) return;
        fetchData();

        const tableTypes = {
            size: {
                renderCell: r => {
                    const d = r.row;
                    return `${FormatBytesM(d.size)}${d.state === 'running' ? '...' : ''}`;
                },
            },
            range: {
                sortComparator: (v1, v2, p1, p2) => {
                    const r1 = p1.api.getRow(p1.id);
                    const r2 = p2.api.getRow(p2.id);
                    const p1Start = _get(r1, 'query.timerange.begin', 0) > 0
                        ? Moment.unix(r1.query.timerange.begin) : 0;
                    const p1End = _get(r1, 'query.timerange.end', 0) > 0
                        ? Moment.unix(r1.query.timerange.end) : 0;

                    const p2Start = _get(r2, 'query.timerange.begin', 0) > 0
                        ? Moment.unix(r2.query.timerange.begin) : 0;
                    const p2End = _get(r2, 'query.timerange.end', 0) > 0
                        ? Moment.unix(r2.query.timerange.end) : 0;

                    return (p1End - p1Start) - (p2End - p2Start);
                },
                renderCell: r => {
                    const d = r.row;
                    if (!_get(d, 'query.timerange')) return '';
                    const start = d.query.timerange.begin > 0
                        ? Moment.unix(d.query.timerange.begin).format(theme.date.long) + ' -' : '';
                    const end = d.query.timerange.end > 0
                        ? Moment.unix(d.query.timerange.end).format(theme.date.long) : '';
                    r.value = `${start}\n${end}`;
                    return <CellExpand {...r} />;
                },
                width: 250,
            },
            query: {
                valueGetter: r => {
                    const d = r.row;
                    return Object.entries(d.params || [])
                        .map(([k, v]) => {
                            let val = v;
                            if (Array.isArray(v)) val = v.join(', ');
                            return `${k}: ${val}`;
                        })
                        .join(', ');
                },
                renderCell: r => {
                    const d = r.row;
                    r.value = Object.entries(d.params || [])
                        .map(([k, v]) => {
                            let vf = v => v;

                            if (k === 'timestamp') {
                                vf = v => {
                                    if (!v) return '';
                                    return Moment.unix(v).format(theme.date.long);
                                };
                            } else if (k === 'tdiff') {
                                vf = v => {
                                    return TimeDiffShort(v);
                                };
                            }

                            let val;
                            if (Array.isArray(v)) {
                                val = v.map(vv => vf(vv)).join(', ');
                            } else val = vf(v);
                            return `${k}: ${val}`;
                        })
                        .join('\n');
                    return <CellExpand {...r} />;
                },
                width: 250,
            },
            progress: {
                renderCell: r => {
                    const d = r.row;
                    const progress = +d.progress || 0;
                    return <ProgressBar value={progress} />;
                },
            },
            created: {
                renderCell: r => {
                    const d = r.row;
                    if (d.created === '0001-01-01T00:00:00Z') return '';
                    const v = Moment(d.created);
                    return v.isValid() ? Moment(v).format(theme.date.long) : v;
                },
                valueGetter: r => {
                    const d = r.row;
                    if (!d.created || d.created === '0001-01-01T00:00:00Z') return '';
                    return d.created;
                },
                width: 150,
            },
            finished: {
                renderCell: r => {
                    const d = r.row;
                    if (d.finished === '0001-01-01T00:00:00Z') return '';
                    const v = Moment(d.finished);
                    return v.isValid() ? Moment(v).format(theme.date.long) : v;
                },
                valueGetter: r => {
                    const d = r.row;
                    if (!d.finished || d.finished === '0001-01-01T00:00:00Z') return '';
                    return d.finished;
                },
                width: 150,
            },
            sensor: {
                renderCell: r => {
                    const v = r.row;
                    return <SensorName uuid={'' + v.sensor_uuid} />;
                },
                sortComparator: (v1, v2) => {
                    const s1 = sensorNames[v1] || { name: v1 };
                    const s2 = sensorNames[v2] || { name: v2 };
                    return s1.name.localeCompare(s2.name);
                },
            },
            state: {
                renderCell: r => {
                    const v = r.row;
                    return <Chip
                        label={v.state}
                        className={clsx(classes.chip, {
                            outlined: v.state === 'empty',
                            done: v.state === 'done',
                        })}
                        variant={v.state === 'empty' ? 'outlined' : undefined}
                        color={
                            v.state === 'running' ? 'secondary'
                                : (v.state === 'done' || v.state === 'empty')
                                    ? 'primary' : 'default'} />;
                },
            },
        };
        setColumns(Fields.map(f => {
            return {
                ...COL_DEFAULT,
                ...f,
                headerName: f.title,
                ...tableTypes[f.variable]
            };
        }));
    }, [sensorNames, classes, fetchData, theme]);

    return (
        <Grid container>
            <Grid item xs={12}>
                <Box mb={3} display="flex" justifyContent="space-between">
                    <SearchBar
                        onSearch={handleSearchChange}
                        searchservice={PcapService}
                        immediate={true} />
                    <Button
                        variant="contained"
                        aria-label="request pcap"
                        onClick={handleOnRequestPcap}>
                        <AddIcon /> Request PCAP
                    </Button>
                </Box>
                <Box height={tableHeight}>
                    <DataGridPro
                        components={{
                            Header: () => (
                                <Grid container
                                    className={classes.toolbar}
                                    alignItems="center"
                                    justifyContent="space-between">
                                    <Typography variant="body2">
                                        Pcap search results will be purged after three days or as space is needed.
                                    </Typography>
                                    <Grid>
                                        <LabeledToolbar>
                                            {MC2CHECK && <PopupMenu
                                                items={Object.entries(cmcs || {})
                                                    .map(([uuid, name]) => ({ uuid, name }))
                                                }
                                                itemkey="uuid"
                                                itemTitle="name"
                                                onSelect={setCmc}
                                                tooltip="cmcs"
                                                aria-label="cmcs"
                                                disabled={!cmcs}
                                                button={<Button size="small" startIcon={<PollIcon />}>
                                                    <p>{cmc ? cmc.name : 'CMC'}</p>
                                                </Button>}
                                                placement={'bottom-end'} />}
                                            {((!MC2CHECK || cmc) && ((pcapPerms.write && selection?.row?.user === user.username) || pcapPerms.override)) && <Button
                                                size="small"
                                                disabled={
                                                    !selection ||
                                                    selection.row.state === 'cancelled' ||
                                                    selection.row.state === 'empty'
                                                }
                                                startIcon={<GetAppIcon />}
                                                onClick={handleOnDownload}
                                                title={selection?.row?.user ? `download "${selection.row.user}"` : "download"}>
                                                <p>Download</p>
                                            </Button>}
                                            {((!MC2CHECK || cmc) && ((pcapPerms.write && selection?.row?.user === user.username) || pcapPerms.override)) && <Button
                                                size="small"
                                                disabled={!selection}
                                                startIcon={<DeleteIcon />}
                                                onClick={() => setDeleteConfrm(true)}
                                                title="delete">
                                                <p>Delete</p>
                                            </Button>}
                                            {(!MC2CHECK || cmc) && <Button
                                                size="small"
                                                disabled={!selection}
                                                startIcon={<RefreshIcon />}
                                                onClick={handleOnRefresh}
                                                title="refresh">
                                                <p>Refresh</p>
                                            </Button>}
                                        </LabeledToolbar>
                                    </Grid>
                                </Grid>
                            ),
                            LoadingOverlay,
                            NoRowsOverlay: MC2CHECK && !cmc
                                ? NoCMCLoadingOverlay : NoRowsOverlay,
                        }}
                        apiRef={tableAPIRef}
                        rows={getFilteredData()}
                        columns={columns}
                        onRowClick={setSelection}
                        sortModel={pcapSort}
                        sortingOrder={['desc', 'asc']}
                        onSortModelChange={handleUpdatePCAPSort}
                        disableMultipleSelection
                        onColumnOrderChange={handleColumnReorder}
                        onColumnResize={handleColumnResize}
                        disableColumnSelector={true}
                        disableColumnMenu={true} />
                </Box>
                <ConfirmationDialog
                    onClose={() => setDeleteConfrm(false)}
                    onOK={handleOnDelete}
                    open={deleteConfrm && selection !== undefined}
                    message={'Delete select Packet Capture?'}
                />
            </Grid>
        </Grid>
    );
};

export default PacketRequests;
